import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { WebAnalytics } from '@/lib/analytics';
import {
    EVENT_CREATE_ITINERARY_LOCATION_ADDED,
    EVENT_CREATE_ITINERARY_LOCATION_MOVED,
    EVENT_CREATE_ITINERARY_LOCATION_REMOVED,
} from '@/lib/analyticsEvents';
import { normalizeCost } from '../utils/costNormalizer';
import type { ItinerarySlot, PlaceUpdate } from '../utils/itinerary/types';
import { ItineraryMode, type ModeMetadata } from '../utils/itinerary/modes';
import { generateDayContainerId } from '../utils/idGeneration';
import { resolveStopMatchIndices, stopIdsMatch } from '@/utils/postLocationStopIdentity';
import { warn } from '@/utils/logger';
import { resequenceTimelineDayDates } from '../utils/itinerary/resequenceTimelineDayDates';
import { UNSCHEDULED_DAY_INDEX, isUnscheduledDayIndex, unscheduledPlacesFromSlots } from '../utils/itinerary/unscheduled';

export interface TimelinePlace {
  id: string;
  title?: string;
  name?: string;
  time?: string;
  address?: string;
  cost?: string | number;
  budget?: string | number;
  notes?: string;
  image?: string;
  image_urls?: string[];
  recommendations?: number;
  activityIcons?: React.ReactNode[];
  sequenceIndex?: number;
  dayIndex?: number;
  locationId?: string;
  placeId?: string; // Google Place ID
  postLocationId?: string;
  slotType?: string;
  timeRange?: string;
  isUserCreated?: boolean;
  _clientId?: string;
  [key: string]: unknown; // Allow additional properties
}

export interface Day {
  containerId: string; // Stable unique identifier for drag-and-drop (session-only, NOT saved to backend)
  date: string;
  /** Structural label (e.g. "Day 1"); do not use for user-authored day notes */
  travelDay?: string;
  /** Per-day notes shown below the day header; persisted as itinerary_data.day_notes */
  dayNote?: string;
  index?: number;
  places?: TimelinePlace[];
  [key: string]: unknown; // Allow additional properties
}

export interface ItineraryContextValue {
  selectedFilter: string;
  setSelectedFilter: (filter: string) => void;
  timelineData: Day[];
  setTimelineData: React.Dispatch<React.SetStateAction<Day[]>>;
  tripTitle: string;
  setTripTitle: (value: unknown) => void;
  itineraryNotes: string;
  setItineraryNotes: (notes: string) => void;
  /** True after the user edits the overview field, including clearing it. */
  itineraryNotesCleared: boolean;
  setItineraryNotesCleared: (cleared: boolean) => void;
  /** True after the user edits a day note, including clearing it. */
  dayNotesEdited: boolean;
  setDayNotesEdited: (edited: boolean) => void;
  /** Owner is looking at the logged-in reader view. Edit chrome stays off; the account stays signed in. */
  readerPreview: boolean;
  setReaderPreview: (preview: boolean) => void;
  currentTripId: string | null;
  showAIAddOverlay: boolean;
  setShowAIAddOverlay: (show: boolean) => void;
  showCustomPlace: boolean;
  setShowCustomPlace: (show: boolean) => void;
  activeDayIndex: number | null;
  setActiveDayIndex: (index: number | null) => void;
  showComingSoonActivityModal: boolean;
  setShowComingSoonActivityModal: (show: boolean) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  city: string;
  setCity: (city: string) => void;
  diffValue: number;
  setDiffValue: (value: number) => void;
  vibeDes: string;
  setVibeDes: (vibe: string) => void;
  itineraryData: ItinerarySlot[];
  setItineraryData: React.Dispatch<React.SetStateAction<ItinerarySlot[]>>;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  // Legacy fields (kept for backwards compatibility)
  remixPostObj: Record<string, unknown> | null;
  setRemixPostObj: (obj: Record<string, unknown> | null) => void;
  currentPost: Record<string, unknown> | null;
  setCurrentPost: (post: Record<string, unknown> | null) => void;
  isEditMode: boolean;
  setIsEditMode: (isEdit: boolean) => void;
  editingPostId: string | null;
  setEditingPostId: (id: string | null) => void;
  
  mode: ItineraryMode;
  modeMetadata: ModeMetadata;
  setMode: (mode: ItineraryMode, metadata?: Partial<ModeMetadata>) => void;
  
  remixDraftId: string | null;
  setRemixDraftId: (id: string | null) => void;
  isPrivate: boolean;
  setIsPrivate: (isPrivate: boolean) => void;
  addPlace: (dayIndex: number, place: TimelinePlace) => void;
  removePlace: (dayIndex: number, placeId: string) => void;
  /** Move a stop to the bottom of another day (timeline and itinerary slots). */
  movePlaceToDay: (fromDayIndex: number, placeId: string, toDayIndex: number) => void;
  /** `target` is the edited card: it picks the right stop when two share an id. */
  updatePlace: (dayIndex: number, placeId: string, updates: PlaceUpdate, target?: TimelinePlace) => void;
  addDay: (dayIndex?: number) => void;
  addDays: (count: number, dayIndex?: number) => void;
  removeDay: (dayIndex: number) => void;
  moveDayToPosition: (fromIndex: number, toIndex: number) => void;
  openAIAddOverlay: (dayIdx?: number) => void;
  closeAIAddOverlay: () => void;
  handleSearchChange: (value: string) => void;
  openCustomPlace: (dayIdx?: number) => void;
  closeCustomPlace: () => void;
  openComingSoonActivityModal: () => void;
  closeComingSoonActivityModal: () => void;
  handleCustomCardClick: () => void;
}

const ItineraryContext = createContext<ItineraryContextValue | undefined>(undefined);

export const useItinerary = () => {
    const context = useContext(ItineraryContext);
    if (!context) {
        throw new Error('useItinerary must be used within an ItineraryProvider');
    }
    return context;
};

// Removed unused sampleTimelineData - commented out entire array
/*
const sampleTimelineData: Day[] = [
    {
        date: 'Sat, Sep 27',
        travelDay: 'Arrival',
        index: 0,
        activities: [
            {
                id: 'a-101',
                title: 'Hôtel San Régis',
                time: '15:00 - 16:00',
                address: '12, Rue Jean Goujon, 8th arr., 75008 Paris, France',
                cost: undefined,
                notes: '',
                recommendations: 4,
                image: 'https://cf.bstatic.com/xdata/images/hotel/max1024x768/520430940.jpg?k=a8c28ef6bfd3eb4c6b7643defe664e6193eb4f5ec7ec57a09f719afcbd4fc675&o=',
                travel: { icon: '�', description: '5 min walk from hotel' },
                index: 0
            },
            {
                id: 'a-102',
                title: 'Cafe de flore',
                time: '17:00 - 18:00',
                address: '172 boulevard St Germain, 75006 Paris, France',
                cost: 50,
                notes: 'Reserve table for 2.',
                recommendations: 12,
                image: 'https://culturetravel.com/wp-content/uploads/2023/08/Cafe-de-Flore-Paris-closed-during-pandemic-IMG_6252.jpg',
                travel: { icon: '�', description: 'Short walking route' },
                index: 1
            },
            {
                id: 'a-103',
                title: 'Eiffel Tower',
                time: '20:00 - 22:00',
                address: 'Champ de Mars, 5 Avenue Anatole France, 75007 Paris, France',
                cost: 0,
                notes: '',
                recommendations: 8,
                image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=800&q=80',
                travel: { icon: '🚗', description: '12 min drive (6 miles)' },
                index: 2
            }
        ]
    },
    {
        date: 'Mon, Sep 28',
        travelDay: 'Explore Paris',
        index: 1,
        activities: [
            {
                id: 'b-201',
                title: 'Louvre Museum',
                icon: '�️',
                time: '09:30 - 12:30',
                address: 'Rue de Rivoli, 75001 Paris, France',
                cost: 25,
                notes: 'Buy timed tickets in advance. See the Mona Lisa and Egyptian antiquities.',
                recommendations: 42,
                image: 'https://dynamic-media.tacdn.com/media/photo-o/2e/ae/c2/aa/caption.jpg?w=1400&h=1000&s=1',
                travel: {
                    icon: '�',
                    description: 'Metro Line 1 to Palais Royal - Musée du Louvre'
                },
                index: 0
            },
            {
                id: 'b-202',
                title: 'Seine River Cruise',
                icon: '🛥️',
                time: '13:00 - 14:30',
                address: 'Port de la Bourdonnais, 75007 Paris, France',
                cost: 18,
                notes: 'Board near Eiffel Tower; choose a glass-covered boat for best views.',
                recommendations: 30,
                image: 'https://dynamic-media.tacdn.com/media/photo-o/2e/a6/d4/94/caption.jpg?w=1400&h=1000&s=1',
                travel: {
                    icon: '🚶',
                    description: '10 min walk from Louvre to boarding point'
                },
                index: 1
            },
            {
                id: 'b-203',
                title: 'Montmartre & Sacré‑Cœur',
                icon: '⛪',
                time: '16:00 - 18:00',
                address: '35 Rue du Chevalier de la Barre, 75018 Paris, France',
                cost: 0,
                notes: "Wander the artists' square; great sunset views from the steps.",
                recommendations: 25,
                image: 'https://images.unsplash.com/photo-1508057198894-247b23fe5ade?auto=format&fit=crop&w=800&q=80',
                travel: { icon: '�', description: 'Metro to Anvers then short walk' },
                index: 2
            }
        ]
    },
    {
        date: 'Tue, Sep 29',
        travelDay: 'Departure',
        index: 2,
        activities: [
            {
                id: 'c-301',
                title: 'Breakfast in Le Marais',
                icon: '☕',
                time: '08:00 - 09:30',
                address: 'Rue des Rosiers, 75004 Paris, France',
                cost: 25,
                notes: 'Try fresh croissants and café au lait at a local boulangerie.',
                recommendations: 10,
                image: 'https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=800&q=80',
                travel: {
                    icon: '🚶',
                    description: 'Walk through charming narrow streets'
                },
                index: 0
            },
            {
                id: 'c-302',
                title: "Musée d'Orsay",
                icon: '🖼️',
                time: '10:00 - 12:30',
                address: "1 Rue de la Légion d'Honneur, 75007 Paris, France",
                cost: 14,
                notes: 'Impressionist collection; check opening hours and special exhibitions.',
                recommendations: 18,
                image: 'https://lh3.googleusercontent.com/gps-cs-s/AC9h4np6D9SNg_2EpjF-SdXlSqzAL5Rm1VOgqMILCR91vysOILBLzSZkWV9We4pZw5Tj2gLIJPc-hlrcfkgWuVI60UqimAmJjmkONzsIrT6GT59evIdrdg9gnYueZB4q7sGFfnLDJ5PaBw=s680-w680-h510-rw',

                travel: { icon: '🚕', description: '20 min taxi to airport later' },
                index: 1
            },
            {
                id: 'c-303',
                title: 'Transfer to Charles de Gaulle',
                icon: '🛫',
                time: '14:00 - 16:00',
                address: 'Charles de Gaulle Airport (CDG)',
                cost: 60,
                notes: 'Allow extra time for security; taxis or Roissybus available.',
                recommendations: 3,
                image: 'https://frenchly.us/wp-content/uploads/elementor/thumbs/shutterstock_1427647196-qoawtpiccx5qgy7qkw6va9poluwwvnapx8dltd4o7c.jpg',
                travel: { icon: '🚗', description: '30-60 min depending on traffic' },
                index: 2
            }
        ]
    }
];
*/

export const ItineraryProvider = ({ children }: { children: ReactNode }) => {
    const [selectedFilter, setSelectedFilter] = useState<string>('overview');
    const [timelineData, setTimelineData] = useState<Day[]>([]);
    const normalizeTripTitle = useCallback((value: unknown): string => {
        if (typeof value === 'string') {
            return value;
        }

        if (value === null || value === undefined) {
            return '';
        }

        if (typeof value === 'object') {
            const valueWithName = value as { name?: string; title?: string };
            if (typeof valueWithName.name === 'string') {
                return valueWithName.name;
            }
            if (typeof valueWithName.title === 'string') {
                return valueWithName.title;
            }

            if (Array.isArray(value)) {
                const firstString = value.find((item) => typeof item === 'string');
                if (typeof firstString === 'string') {
                    return firstString;
                }
            }
        }

        try {
            return String(value);
        } catch {
            return '';
        }
    }, []);

    const [tripTitle, setTripTitleState] = useState<string>('');
    const setTripTitle = useCallback((value: unknown) => {
        const normalized = normalizeTripTitle(value);
        setTripTitleState(normalized);
    }, [normalizeTripTitle]);
    const [itineraryNotes, setItineraryNotes] = useState<string>('');
    const [itineraryNotesCleared, setItineraryNotesCleared] = useState(false);
    const [dayNotesEdited, setDayNotesEdited] = useState(false);
    const [readerPreview, setReaderPreview] = useState(false);
    const [currentTripId] = useState<string | null>(null);

    const [showAIAddOverlay, setShowAIAddOverlay] = useState(false);
    const [showCustomPlace, setShowCustomPlace] = useState(false);
    const [activeDayIndex, setActiveDayIndex] = useState<number | null>(null);
    const [showComingSoonActivityModal, setShowComingSoonActivityModal] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [city, setCity] = useState<string>('');
    const [diffValue, setDiffValue] = useState<number>(3);
    const [vibeDes, setVibeDes] = useState<string>('');
    const [itineraryData, setItineraryData] = useState<ItinerarySlot[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [remixPostObj, setRemixPostObj] = useState<Record<string, unknown> | null>(null);
    const [currentPost, setCurrentPost] = useState<Record<string, unknown> | null>(null);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [editingPostId, setEditingPostId] = useState<string | null>(null);
    
    const [mode, setModeState] = useState<ItineraryMode>(ItineraryMode.NEW);
    const [modeMetadata, setModeMetadataState] = useState<ModeMetadata>({
      mode: ItineraryMode.NEW,
      sourcePostId: null,
      sourcePostData: null,
      targetPostId: null,
      isDraft: false,
    });
    
    // Set mode with optional metadata update
    const setMode = useCallback((newMode: ItineraryMode, metadata?: Partial<ModeMetadata>) => {
      setModeState(newMode);
      if (metadata) {
        setModeMetadataState((prev) => ({
          ...prev,
          ...metadata,
          mode: newMode,
        } as ModeMetadata));
      } else {
        // Update mode in metadata
        setModeMetadataState((prev) => ({
          ...prev,
          mode: newMode,
        } as ModeMetadata));
      }
    }, []);
    
    const [remixDraftId, setRemixDraftId] = useState<string | null>(null);
    const [isPrivate, setIsPrivate] = useState<boolean>(false);
    const addPlace = useCallback((dayIndex: number, place: TimelinePlace) => {
        const makeId = () => {
            if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
                const cryptoWithUUID = crypto as { randomUUID: () => string };
                return cryptoWithUUID.randomUUID();
            }
            return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        };

        setTimelineData((prevData) => {
            const newData = [...(prevData || [])];
            
            // Ensure array is long enough for dayIndex
            // If dayIndex is beyond current array length, extend array with empty days
            // This handles edge cases where days are added dynamically
            while (newData.length <= dayIndex) {
                newData.push({ 
                    containerId: generateDayContainerId(),
                    date: '', 
                    places: [],
                    index: newData.length
                });
            }
            
            // Get existing day or create new one
            // Preserve all existing day properties (date, travelDay, etc.) from wizard initialization
            const existingDay = newData[dayIndex];
            const day = existingDay ? {
                ...existingDay, // Preserve all properties (date, travelDay, index, containerId, etc.)
                places: existingDay.places || [], // Ensure places array exists
            } : { 
                containerId: generateDayContainerId(),
                date: '', 
                places: [],
                index: dayIndex
            };
            
            // Ensure day has required structure (defensive check)
            if (!day.places) {
                day.places = [];
            }
            if (day.index === undefined) {
                day.index = dayIndex;
            }

            const last = (day.places || [])[(day.places || []).length - 1];
            if (place) {
                if (place._clientId) {
                    const exists = (day.places || []).some((a: TimelinePlace) => a && a._clientId === place._clientId);
                    if (exists) return prevData;
                }
                // Check for duplicate by id (more reliable than title/time/address)
                if (place.id) {
                    const existsById = (day.places || []).some((a: TimelinePlace) => a && a.id === place.id);
                    if (existsById) {
                        return prevData;
                    }
                }
                if (
                    last &&
                    last.title === place.title &&
                    (last.time || '') === (place.time || '') &&
                    (last.address || '') === (place.address || '')
                ) {
                    return prevData;
                }
            }

            // Preserve existing id if provided (for ID sync with itineraryData)
            // Otherwise generate new UUID
            // This ensures id in itineraryData can match id in timelineData (removed activityId)
            const newPlace = {
                ...place,
                id: place.id || makeId(), // Preserve id if already set, otherwise generate
                index: 0
            };

            // Append new place to the end of the places array so the day's existing order
            // (and the derived cover photo) is unchanged.
            const existingPlaces = (day.places || []).map((p: TimelinePlace, idx: number) => ({
                ...p,
                index: idx
            }));
            const updatedPlaces = [
                ...existingPlaces,
                { ...newPlace, index: existingPlaces.length },
            ];
            
            // Update the day in the array, preserving all existing properties
            // This ensures wizard-initialized days keep their date, travelDay, etc.
            newData[dayIndex] = {
                ...day, // Preserve all existing properties (date, travelDay, etc.)
                places: updatedPlaces, // Update places array
                index: dayIndex, // Ensure index is set correctly
            };

            WebAnalytics.trackContentCreation(EVENT_CREATE_ITINERARY_LOCATION_ADDED, {
                itinerary_id: editingPostId ?? undefined,
                itinerary_name: tripTitle,
                place_id: newPlace.placeId || newPlace.locationId,
                place_name: (newPlace.name || newPlace.title) ?? undefined,
                is_user_created: !!newPlace.isUserCreated,
                surface: 'itinerary',
                day_index: dayIndex,
            });

            return newData;
        });
    }, [editingPostId, tripTitle]);

    const removePlace = useCallback((dayIndex: number, placeId: string) => {
        // Capture metadata from timelineData BEFORE deletion
        // We need sequenceIndex, locationId, and placeId for matching in itineraryData
        // Unscheduled stops live only in itineraryData.
        const places = isUnscheduledDayIndex(dayIndex)
            ? unscheduledPlacesFromSlots(itineraryData)
            : timelineData?.[dayIndex]?.places;
        if (!places) {
            return;
        }

        const place = places.find((p: TimelinePlace) =>
            p.id === placeId ||
            p.postLocationId === placeId ||
            String(p.id) === String(placeId) ||
            String(p.postLocationId) === String(placeId)
        );
        
        if (!place) {
            return;
        }

        WebAnalytics.trackContentCreation(EVENT_CREATE_ITINERARY_LOCATION_REMOVED, {
            itinerary_id: editingPostId ?? undefined,
            itinerary_name: tripTitle,
            place_id: place.placeId || place.locationId,
            place_name: (place.name || place.title) ?? undefined,
            is_user_created: !!place.isUserCreated,
            surface: 'itinerary',
            day_index: dayIndex,
        });

        const placeSequenceIndex = place ? (typeof place.sequenceIndex === 'number' ? place.sequenceIndex : -1) : -1;
        const placeLocationId = place?.locationId || null;
        const placePlaceId = place?.placeId || null;
        
        // Second pass: actually remove from timelineData
        setTimelineData((prevData) => {
            const newData = [...(prevData || [])];
            if (newData[dayIndex]) {
                newData[dayIndex] = {
                    ...newData[dayIndex],
                    places: (newData[dayIndex].places || []).filter(
                        (place) => {
                            // Removed activityId - use id and postLocationId for matching
                            const matches = place.id !== placeId && 
                                          place.postLocationId !== placeId &&
                                          String(place.id) !== String(placeId) &&
                                          String(place.postLocationId) !== String(placeId);
                            return matches;
                        }
                    )
                };
            }
            return newData;
        });

        // Also remove from itineraryData using captured metadata
        setItineraryData((prevItineraryData: ItinerarySlot[]) => {
            const updatedItineraryData = [...(prevItineraryData || [])];
            
            // Itineraries always check dayIndex - Lists use ListContext instead
            const filtered = updatedItineraryData.filter((slot) => {
                // Match by postLocationId (PostLocation _id), _id, locationId, placeId, or sequenceIndex+dayIndex
                const slotLocationId = slot?.locationId || slot?.location_id;
                const slotPostLocationId = slot?.postLocationId;
                const slotId = slot?._id;
                const slotSequenceIndex = typeof slot?.sequenceIndex === 'number' ? slot.sequenceIndex : -1;
                const slotPlaceId = slot?.placeId || null;
                
                // Handle placeId that might be in format "post-location-${postLocationId}" or direct PostLocation _id
                const placeIdStr = String(placeId);
                const isPostLocationPrefix = placeIdStr.startsWith('post-location-');
                const extractedPostLocationId = isPostLocationPrefix 
                    ? placeIdStr.replace('post-location-', '')
                    : placeIdStr;
                
                // For Itineraries, always check dayIndex matches
                const dayIndexMatches = slot?.dayIndex === dayIndex;
                
                const matchesPostLocationId = slotPostLocationId && (
                    String(slotPostLocationId) === placeIdStr ||
                    String(slotPostLocationId) === extractedPostLocationId
                );
                const matchesId = slotId && (
                    String(slotId) === placeIdStr ||
                    String(slotId) === extractedPostLocationId ||
                    (isPostLocationPrefix && String(slotPostLocationId) === extractedPostLocationId)
                );
                const matchesLocationId = slotLocationId && (
                    String(slotLocationId) === placeIdStr ||
                    (placeLocationId && String(slotLocationId) === String(placeLocationId))
                );
                const matchesPlaceId = slotPlaceId && placePlaceId && String(slotPlaceId) === String(placePlaceId);
                
                // Also match by sequenceIndex + dayIndex for new posts
                // This handles cases where _id isn't set yet (new posts before saving)
                const matchesSequenceIndex = dayIndexMatches && 
                    placeSequenceIndex >= 0 && 
                    slotSequenceIndex === placeSequenceIndex;
                
                // Don't remove if it matches the place we want to delete
                if (dayIndexMatches) {
                    // Priority 1: Match by postLocationId (PostLocation _id) - most reliable for existing posts
                    if (matchesPostLocationId) {
                        return false; // Remove this slot
                    }
                    // Priority 2: Match by _id (slot identifier, renamed from activityId)
                    if (matchesId) {
                        return false; // Remove this slot
                    }
                    // Priority 3: Match by locationId
                    if (matchesLocationId) {
                        return false; // Remove this slot
                    }
                    // Priority 4: Match by placeId (Google Place ID)
                    if (matchesPlaceId) {
                        return false; // Remove this slot
                    }
                    // Priority 5: Match by sequenceIndex + dayIndex (for new posts without _id)
                    if (matchesSequenceIndex) {
                        return false; // Remove this slot
                    }
                }
                return true; // Keep this slot
            });

            return filtered;
        });
    }, [setItineraryData, timelineData, itineraryData, editingPostId, tripTitle]);

    /** Either day may be UNSCHEDULED_DAY_INDEX: unscheduled stops have a slot but no timeline entry. */
    const movePlaceToDay = useCallback((fromDayIndex: number, placeId: string, toDayIndex: number) => {
        if (fromDayIndex === toDayIndex) return;
        const fromUnscheduled = isUnscheduledDayIndex(fromDayIndex);
        const toUnscheduled = isUnscheduledDayIndex(toDayIndex);
        const fromPlaces = fromUnscheduled
            ? unscheduledPlacesFromSlots(itineraryData)
            : timelineData?.[fromDayIndex]?.places;
        if (!fromPlaces || (!toUnscheduled && !timelineData?.[toDayIndex])) return;
        const place = fromPlaces.find((p) => stopIdsMatch(p, placeId));
        if (!place) return;

        // Append at the bottom of the target day, numbered after its last slot (same rule as adding a place).
        const nextSequenceIndex = itineraryData.reduce((max, slot) => {
            if (slot.dayIndex !== toDayIndex) return max;
            const seq = typeof slot.sequenceIndex === 'number' ? slot.sequenceIndex : 0;
            return Math.max(max, seq);
        }, -1) + 1;

        setTimelineData((prevData) => {
            const newData = [...(prevData || [])];
            if (!fromUnscheduled) {
                const source = newData[fromDayIndex];
                if (!source) return prevData;
                newData[fromDayIndex] = {
                    ...source,
                    places: (source.places || []).filter((p) => !stopIdsMatch(p, placeId)),
                };
            }
            if (!toUnscheduled) {
                const target = newData[toDayIndex];
                if (!target) return prevData;
                const targetPlaces = target.places || [];
                newData[toDayIndex] = {
                    ...target,
                    places: [
                        ...targetPlaces,
                        {
                            ...place,
                            dayIndex: toDayIndex,
                            sequenceIndex: nextSequenceIndex,
                            index: targetPlaces.length,
                        },
                    ],
                };
            }
            return newData;
        });

        setItineraryData((prevItineraryData: ItinerarySlot[]) =>
            (prevItineraryData || []).map((slot) =>
                slot?.dayIndex === fromDayIndex && stopIdsMatch(slot, placeId)
                    ? { ...slot, dayIndex: toDayIndex, sequenceIndex: nextSequenceIndex }
                    : slot
            )
        );

        WebAnalytics.trackContentCreation(EVENT_CREATE_ITINERARY_LOCATION_MOVED, {
            itinerary_id: editingPostId ?? undefined,
            itinerary_name: tripTitle,
            place_id: place.placeId || place.locationId,
            place_name: (place.name || place.title) ?? undefined,
            is_user_created: !!place.isUserCreated,
            surface: 'itinerary',
            from_day_index: fromDayIndex,
            to_day_index: toDayIndex,
        });
    }, [timelineData, itineraryData, editingPostId, tripTitle]);

    const addDays = useCallback((count: number, dayIndex?: number) => {
        let insertedMidList = false;
        const effectiveCount = Math.max(1, Math.round(count));

        setTimelineData((prevData) => {
            const newData = [...(prevData || [])];
            const targetIndex = dayIndex !== undefined ? dayIndex : newData.length;
            const isUndated = newData.every((d) => !d.date?.trim());

            const newDaysArr = Array.from({ length: effectiveCount }, (_, i) => ({
                containerId: generateDayContainerId(),
                index: targetIndex + i,
                date: '',
                travelDay: `Day ${targetIndex + i + 1}`,
                places: [],
            } as Day));

            if (targetIndex < newData.length) {
                insertedMidList = true;
                newData.splice(targetIndex, 0, ...newDaysArr);
            } else {
                newData.push(...newDaysArr);
            }

            newData.forEach((day, idx) => {
                day.index = idx;
            });

            if (isUndated) {
                newData.forEach((day, idx) => {
                    day.date = '';
                    day.travelDay = `Day ${idx + 1}`;
                });
            } else {
                resequenceTimelineDayDates(newData);
                newData.forEach((day, idx) => {
                    day.travelDay = `Day ${idx + 1}`;
                });
            }

            return newData;
        });

        if (dayIndex !== undefined && insertedMidList) {
            setItineraryData((prevSlots) =>
                prevSlots.map((slot) =>
                    slot.dayIndex >= dayIndex
                        ? { ...slot, dayIndex: slot.dayIndex + effectiveCount }
                        : slot
                )
            );
        }
    }, [setItineraryData]);

    const addDay = useCallback((dayIndex?: number) => {
        addDays(1, dayIndex);
    }, [addDays]);

    const removeDay = useCallback((dayIndex: number) => {
        setTimelineData((prevData) => {
            const newData = [...(prevData || [])];
            if (dayIndex < 0 || dayIndex >= newData.length) {
                return newData;
            }

            // Remove the day
            newData.splice(dayIndex, 1);
            
            // Reindex remaining days
            newData.forEach((day, idx) => {
                day.index = idx;
                if (day.travelDay) {
                    if (idx === 0) {
                        day.travelDay = 'Arrival';
                    } else if (idx === newData.length - 1) {
                        day.travelDay = 'Departure';
                    } else {
                        day.travelDay = `Day ${idx + 1}`;
                    }
                }
            });
            
            return newData;
        });
        
        // The deleted day's stops move to the bottom of Unscheduled (in their day order);
        // slots after the deleted day shift up one day.
        setItineraryData((prevSlots) => {
            let nextUnscheduledSequence = prevSlots.reduce((max, slot) => {
                if (!isUnscheduledDayIndex(slot.dayIndex)) return max;
                const seq = typeof slot.sequenceIndex === 'number' ? slot.sequenceIndex : 0;
                return Math.max(max, seq);
            }, -1) + 1;
            const unscheduledSequenceBySlot = new Map<ItinerarySlot, number>();
            prevSlots
                .filter((slot) => slot.dayIndex === dayIndex)
                .sort((a, b) => (a.sequenceIndex ?? 0) - (b.sequenceIndex ?? 0))
                .forEach((slot) => unscheduledSequenceBySlot.set(slot, nextUnscheduledSequence++));

            return prevSlots.map((slot) => {
                const unscheduledSequence = unscheduledSequenceBySlot.get(slot);
                if (unscheduledSequence !== undefined) {
                    return {
                        ...slot,
                        dayIndex: UNSCHEDULED_DAY_INDEX,
                        sequenceIndex: unscheduledSequence,
                    };
                }
                if (slot.dayIndex > dayIndex) {
                    return {
                        ...slot,
                        dayIndex: slot.dayIndex - 1,
                    };
                }
                return slot;
            });
        });
    }, []);

    const moveDayToPosition = useCallback((fromIndex: number, toIndex: number) => {
        if (fromIndex === toIndex) return;

        setTimelineData((prev) => {
            const newData = [...prev];
            const [movedDay] = newData.splice(fromIndex, 1);
            newData.splice(toIndex, 0, movedDay);
            newData.forEach((day, idx) => {
                day.index = idx;
                if (idx === 0) {
                    day.travelDay = 'Arrival';
                } else if (idx === newData.length - 1) {
                    day.travelDay = 'Departure';
                } else {
                    day.travelDay = `Day ${idx + 1}`;
                }
            });
            return newData;
        });

        setItineraryData((prevSlots) =>
            prevSlots.map((slot) => {
                if (slot.dayIndex === fromIndex) {
                    return { ...slot, dayIndex: toIndex };
                }
                if (fromIndex < toIndex) {
                    if (slot.dayIndex > fromIndex && slot.dayIndex <= toIndex) {
                        return { ...slot, dayIndex: slot.dayIndex - 1 };
                    }
                } else {
                    if (slot.dayIndex >= toIndex && slot.dayIndex < fromIndex) {
                        return { ...slot, dayIndex: slot.dayIndex + 1 };
                    }
                }
                return slot;
            })
        );
    }, []);

        const updatePlace = useCallback((dayIndex: number, placeId: string, updates: PlaceUpdate, target?: TimelinePlace) => {
            const hasCostUpdate = updates?.cost !== undefined || updates?.budget !== undefined;
            const normalizedCost = hasCostUpdate
                ? normalizeCost(
                      updates?.cost !== undefined ? updates.cost : updates?.budget,
                      {
                          // No made-up default: an empty cost stays empty and the card offers "Add cost".
                          fallbackToDefault: false,
                          preserveTextOnNaN: true
                      }
                  )
                : null;


            const hasPlaceIdentityUpdate =
                updates?.isUserCreated !== undefined ||
                updates?.placeId !== undefined ||
                updates?.slotType !== undefined ||
                updates?.locationData !== undefined ||
                updates?.image_urls !== undefined ||
                updates?.location !== undefined ||
                updates?.image !== undefined;

            const hasSlotUpdates =
                updates?.notes !== undefined ||
                updates?.address !== undefined ||
                updates?.title !== undefined ||
                updates?.name !== undefined ||
                hasCostUpdate ||
                updates?.timeRange !== undefined ||
                updates?.time !== undefined ||
                hasPlaceIdentityUpdate;

            const nextTitle =
                updates?.title !== undefined || updates?.name !== undefined
                    ? String(
                          updates.title !== undefined ? updates.title : updates.name ?? ''
                      ).trim()
                    : undefined;

            const timeRangeValue =
                updates?.timeRange !== undefined || updates?.time !== undefined
                    ? (updates?.timeRange || updates?.time)
                    : undefined;

            const costStorageValue = hasCostUpdate && normalizedCost
                ? normalizedCost.storageValue
                : undefined;
            const costForSlot = hasCostUpdate && normalizedCost
                ? (normalizedCost.numericValue !== null
                    ? normalizedCost.numericValue
                    : normalizedCost.storageValue)
                : undefined;

            setTimelineData((prevData) => {
                const newData = [...(prevData || [])];
                const day = newData[dayIndex];
                if (!day) {
                    return newData;
                }

                const places = Array.isArray(day.places) ? [...day.places] : [];
                const matchedIndices = resolveStopMatchIndices(places, placeId, target);
                if (matchedIndices.length === 0) {
                    return newData;
                }
                if (matchedIndices.length > 1) {
                    warn('[ItineraryContext] updatePlace: several stops share one id', { dayIndex, placeId });
                }

                const updatedPlaces = places.map((place: TimelinePlace, index: number) => {
                    if (!matchedIndices.includes(index)) {
                        return place;
                    }
                    const updatesForPlace = { ...(updates || {}) };
                    if (hasCostUpdate && normalizedCost) {
                        updatesForPlace.cost = normalizedCost.storageValue;
                        updatesForPlace.budget = normalizedCost.storageValue;
                    }
                    return { ...place, ...updatesForPlace };
                });

                newData[dayIndex] = {
                    ...day,
                    places: updatedPlaces,
                };
                return newData;
            });

            if (hasSlotUpdates) {
                setItineraryData((prevItineraryData: ItinerarySlot[]) => {
                    const slots = prevItineraryData || [];
                    const matchedSlotIndices = resolveStopMatchIndices(
                        slots,
                        placeId,
                        target,
                        (slot) => slot.dayIndex === dayIndex,
                    );
                    return slots.map((slot, slotIndex) => {
                        if (!matchedSlotIndices.includes(slotIndex)) {
                            return slot;
                        }

                        const slotPatch: Partial<ItinerarySlot> = {};

                        if (updates?.notes !== undefined) {
                            slotPatch.notes = updates.notes as string | undefined;
                        }
                        if (updates?.address !== undefined) {
                            slotPatch.address = updates.address as string | undefined;
                        }
                        if (nextTitle !== undefined) {
                            slotPatch.name = nextTitle;
                            (slotPatch as Record<string, unknown>).body = nextTitle;
                        }
                        if (hasCostUpdate && costStorageValue !== undefined) {
                            slotPatch.budget = costStorageValue;
                            (slotPatch as Record<string, unknown>).cost = costForSlot;
                            slotPatch.locationData = slot.locationData
                                ? { ...slot.locationData, budget: costStorageValue }
                                : slot.locationData;
                        }
                        if (timeRangeValue !== undefined) {
                            slotPatch.timeRange = timeRangeValue as string | undefined;
                            (slotPatch as Record<string, unknown>).time = timeRangeValue;
                        }
                        if (updates?.isUserCreated !== undefined) {
                            slotPatch.isUserCreated = Boolean(updates.isUserCreated);
                        }
                        if (typeof updates?.placeId === 'string') {
                            slotPatch.placeId = updates.placeId;
                        }
                        if (typeof updates?.slotType === 'string') {
                            slotPatch.slotType = updates.slotType;
                        }
                        if (updates?.locationData !== undefined) {
                            slotPatch.locationData = updates.locationData as ItinerarySlot['locationData'];
                            const locationId = (updates.locationData as { _id?: string } | null)?._id;
                            if (locationId) {
                                slotPatch.locationId = locationId;
                                slotPatch.location_id = locationId;
                            }
                        }
                        if (Array.isArray(updates?.image_urls)) {
                            slotPatch.image_urls = updates.image_urls as string[];
                        }
                        if (updates?.location && typeof updates.location === 'object') {
                            slotPatch.location = updates.location as ItinerarySlot['location'];
                        }
                        if (typeof updates?.image === 'string') {
                            (slotPatch as Record<string, unknown>).image = updates.image;
                        }

                        return { ...slot, ...slotPatch };
                    });
                });
            }
        }, [setTimelineData, setItineraryData]);

    const openAIAddOverlay = useCallback((dayIdx?: number) => {
        setActiveDayIndex(typeof dayIdx === 'number' ? dayIdx : null);
        setShowAIAddOverlay(true);
    }, []);
    const closeAIAddOverlay = useCallback(() => {
        setActiveDayIndex(null);
        setShowAIAddOverlay(false);
        setSearchValue('');
    }, []);

    const openCustomPlace = useCallback((dayIdx?: number) => {
        setActiveDayIndex(typeof dayIdx === 'number' ? dayIdx : null);
        setShowCustomPlace(true);
    }, []);
    const closeCustomPlace = useCallback(() => {
        setShowCustomPlace(false);
        setActiveDayIndex(null);
    }, []);

    const openComingSoonActivityModal = useCallback(() => setShowComingSoonActivityModal(true), []);
    const closeComingSoonActivityModal = useCallback(() => setShowComingSoonActivityModal(false), []);

    const handleSearchChange = useCallback((value: string) => {
        setSearchValue(value);
    }, []);

    const handleCustomCardClick = useCallback(() => {
        closeAIAddOverlay();
        openCustomPlace();
    }, [closeAIAddOverlay, openCustomPlace]);

    const value = {
        selectedFilter,
        timelineData,
        tripTitle,
        itineraryNotes,
        setItineraryNotes,
        itineraryNotesCleared,
        setItineraryNotesCleared,
        dayNotesEdited,
        setDayNotesEdited,
        readerPreview,
        setReaderPreview,
        currentTripId,
        showAIAddOverlay,
        showCustomPlace,
        searchValue,
        setSelectedFilter,
        setTimelineData,
        setTripTitle,
        addPlace,
        removePlace,
        movePlaceToDay,
        updatePlace,
        addDay,
        addDays,
        removeDay,
        moveDayToPosition,
        openAIAddOverlay,
        closeAIAddOverlay,
        openCustomPlace,
        closeCustomPlace,
        activeDayIndex,
        showComingSoonActivityModal,
        setShowComingSoonActivityModal,
        openComingSoonActivityModal,
        closeComingSoonActivityModal,
        handleSearchChange,
        handleCustomCardClick,
        setCity,
        city,
        diffValue,
        setDiffValue,
        setVibeDes,
        vibeDes,
        itineraryData,
        setItineraryData,
        isLoading,
        setIsLoading,
        setRemixPostObj,
        remixPostObj,
        currentPost,
        setCurrentPost,
        isEditMode,
        setIsEditMode,
        editingPostId,
        setEditingPostId,
        mode,
        modeMetadata,
        setMode,
        remixDraftId,
        setRemixDraftId,
        isPrivate,
        setIsPrivate,
        setShowAIAddOverlay,
        setShowCustomPlace,
        setActiveDayIndex,
        setSearchValue
    };

    return <ItineraryContext.Provider value={value}>{children}</ItineraryContext.Provider>;
};

export default ItineraryContext;
